async function readJson(response, fallbackMessage) {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(payload.error || payload.message || fallbackMessage);
        error.status = response.status;
        error.data = payload.data;
        throw error;
    }
    return payload;
}

export function createSellerMediaApi({
    fetch,
    apiUrl = '',
    xhrFactory = () => new XMLHttpRequest(),
}) {
    const baseUrl = `${apiUrl}/v1/seller/media`;

    return {
        async list() {
            const response = await fetch(baseUrl);
            const payload = await readJson(response, 'Unable to load the media library');
            return payload.data || [];
        },

        async get(uuid) {
            const response = await fetch(`${baseUrl}/${encodeURIComponent(uuid)}`);
            const payload = await readJson(response, 'Unable to load media details');
            return payload.data;
        },

        async update(uuid, values) {
            const response = await fetch(`${baseUrl}/${encodeURIComponent(uuid)}`, {
                method: 'PATCH',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(values),
            });
            const payload = await readJson(response, 'Unable to save media details');
            return payload.data;
        },

        async rotate(uuid) {
            const response = await fetch(`${baseUrl}/${encodeURIComponent(uuid)}/rotate`, {
                method: 'POST',
            });
            const payload = await readJson(response, 'Unable to rotate the image');
            return payload.data;
        },

        async delete(uuid) {
            const response = await fetch(`${baseUrl}/${encodeURIComponent(uuid)}`, {
                method: 'DELETE',
            });
            const payload = await readJson(response, 'Unable to delete the file');
            return payload.data;
        },

        async upload(file, onProgress = () => {}) {
            const initializeResponse = await fetch(`${baseUrl}/upload-get-url`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    filename: file.name,
                    mimetype: file.type || 'application/octet-stream',
                    size: file.size,
                }),
            });
            const upload = await readJson(
                initializeResponse,
                'Unable to prepare the upload',
            );

            await new Promise((resolve, reject) => {
                const xhr = xhrFactory();
                xhr.open('PUT', upload.uploadUrl, true);
                xhr.setRequestHeader('Content-Type', upload.mimetype);
                xhr.upload.addEventListener('progress', event => {
                    if (event.lengthComputable) {
                        onProgress(Math.round((event.loaded / event.total) * 100));
                    }
                });
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                        return;
                    }
                    reject(new Error('The file could not be uploaded to storage'));
                };
                xhr.onerror = () => reject(new Error('The upload connection failed'));
                xhr.onabort = () => reject(new Error('The upload was canceled'));
                xhr.ontimeout = () => reject(new Error('The upload timed out'));
                xhr.send(file);
            });

            const finalizeResponse = await fetch(`${baseUrl}/upload-finalize`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(upload),
            });
            const finalized = await readJson(
                finalizeResponse,
                'Unable to finish the upload',
            );
            onProgress(100);
            return finalized.file || finalized.data;
        },
    };
}
